import { Cards, CourseChapter, CourseModuleCards } from "@mikros/library";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IconName,
  IconPrefix,
  IconProp,
} from "@fortawesome/fontawesome-svg-core";
import Picker from "@emoji-mart/react";
import CharacterCount from "@tiptap/extension-character-count";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Confirm } from "../confirm";
import { Button } from "@progress/kendo-react-buttons";
import { SortableItemUIProps } from "@progress/kendo-react-sortable";
import { TextBox, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { Tooltip, Whisper } from "rsuite";
import HardBreak from "@tiptap/extension-hard-break";
import { useIntl } from "react-intl";
import { Alert } from "../alert";
import { Textarea } from "@mui/joy";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { text } from "stream/consumers";
import { Loading } from "../loading";

// Types and Interfaces
export interface CardComponentProps extends SortableItemUIProps {
  showCardOptions: boolean;
  maxCards: number;
  cardType?: string;
  removeCard: (e: string) => void;
  index: (cardId: string) => number;
  handleCardContentChange: (cardId: string, content?: string) => void;
  handleSpecificCardUpdate?: (e: CourseModuleCards) => any;
  handleCardImageUpdate?: (cardId: string, imageUrl?: any) => void;
  handleCardVideoUpdate: (cardId: string, videoUrl?: any) => void;
  handleGenVideoUrl: (cardId: string, url: any, presignedUrl: any) => void;
  handleGenImageUrl: (cardId: string, url: any) => void;
  handleGenAudioUrl: (cardId: string, url: any, updatedCardData: any) => void;
  handleCardAudioUpdate: (cardId: string, audioUrl?: any) => void;
  handleDuplicateCard: (e: string) => void;
  handleGenerateAiVideo?: (videoPromptText: string) => void;
  handleGenerateAiImage?: (imagePromptText: string) => void;
  handleGenerateAiAudio?: (audioPromptText: string) => void;
  handleVideoGeneration?: (e: any) => Promise<string | false>;
  handleImageGeneration?: (e: any) => Promise<string | false>;
  handleCardUpdate?: (updatedCard: any) => void; //

  // handleAudioGeneration?: (e: any) => Promise<string | false>;

  handleAudioGeneration?: (
    e: any
  ) => Promise<{ presignedUrl: string; prompt: string } | false>;
  enableDrag: boolean;
  handleEnableDrag: (e: boolean) => void;
  cards: CourseModuleCards;
  generateUrl?: (courseId: any, filepath: string, cardId: string) => void;
  resultUrl?: any;
  resultApi?: any;
  videoGeneratingCardIds: Set<string>;
  setVideoGeneratingCardIds?: (cardId: string | null) => void;
  handleCardTypeUpdate?: (cardId: string, newCardType: string) => void;
  handleFileTypeUpdate?: (cardId: string, fileType: string) => void;
  // quickSave:Function
  quickSave?: (
    updatedChapter?: CourseChapter,
    forceAIVideoGenerating?: boolean
  ) => Promise<void>;

  chapterName?: any;
  warningDialogFunc?: Function;
  aIVideoGenerating: boolean;
  setAIVideoGenerating: (e: boolean) => void;
}

export interface MenuBarProps {
  [x: string]: any;
  editor: Editor;
  cardType?: string;
  emojiPicker: () => void;
  deleteFunc: () => void;
  textToVideo: () => void;
  textToImage: () => void;
  textToAudio: () => void;
  duplicateFunc: () => void;
  imageUpload: () => void;
  videoUpload: () => void;
  audioUpload: () => void;
  enableDrag: (e: boolean) => void;
  tooltip: any;
  selectedImage: any;
  selectedVideo: any;
  status?: any;
  videoGenerationValue: boolean;
  isDuplicateDisabled?: boolean;
  chapterName: string;
}

// Constants
const CHARACTER_LIMIT = 800;
const FILE_SIZE_LIMIT = 15360;
const CARD_TYPES = {
  TEXT: "TEXT",
  IMG: "IMG",
  VIDEO: "VIDEO",
  AUDIO: "AUDIO",
  LINK: "LINK",
} as const;

// Utility Functions
const whatsappToHTML = (text: string): string => {
  if (!text) return "";
  return text
    .replace(/(\\n\\n)/g, "<p></p>")
    .replace(/\*([^*]+)\*/g, "<strong>$1</strong>");
};

const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1) : "";
};

const createFilePath = (
  accId: string,
  courseId: string,
  chapterId: string,
  cardId: string,
  extension: string
): string =>
  `Mikros-Cards/acct-${accId}/crse-${courseId}/chptr-${chapterId}/card-${cardId}/${cardId}.${extension}`;

// MenuBar Component
const MenuBar = (editor: MenuBarProps) => {
  const intl = useIntl();
  const [selectState, setSelectState] = useState<string>("");
  const [isTextToVideoHovered, setIsTextToVideoHovered] = useState(false);
  const [isTextToImageHovered, setIsTextToImageHovered] = useState(false);
  const [isTextToAudioHovered, setIsTextToAudioHovered] = useState(false);

  if (!editor.editor) return null;

  const isDisabled = editor.videoGenerationValue;
  const trigger = isDisabled ? undefined : "hover";

  console.log("enable drag value", editor.enableDrag);

  const createTooltip = (messageId: string) => (
    <Tooltip
      className="bg-mk-primary text-black opacity-65"
      style={{ zIndex: 99999 }}
    >
      <div>{intl.formatMessage({ id: messageId })}</div>
    </Tooltip>
  );

  const createDragButton = (
    icon: [string, string],
    messageId: string,
    action: () => void,
    isActive: boolean = false,
    disabled: boolean = isDisabled
  ) => (
    <Whisper
      placement="top"
      controlId="control-img-hover"
      trigger="hover"
      // speaker={<Tooltip>Drag</Tooltip>}
      speaker={createTooltip(messageId)}
    >
      {(triggerProps, ref) => (
        <div
          {...triggerProps}
          ref={ref}
          style={{ pointerEvents: "auto" }} // Force it to allow events
          onClick={() => {
            setSelectState("arrow");
            // editor.enableDrag(true);
            // editor.editor.setEditable(false);
          }}
          className="cursor-pointer p-1"
        >
          <FontAwesomeIcon
            icon={["far", "arrows-up-down-left-right"]}
            className="w-4 h-4 text-mk-primary hover:text-mk-primary-bright"
          />
        </div>
      )}
    </Whisper>
  );

  const createIconButton = (
    icon: [string, string],
    messageId: string,
    action: () => void,
    isActive: boolean = false,
    disabled: boolean = isDisabled
  ) => (
    <Whisper
      placement="top"
      controlId="control-img-hover"
      trigger={trigger}
      speaker={createTooltip(messageId)}
    >
      {(triggerProps, ref) => (
        <FontAwesomeIcon
          {...triggerProps}
          ref={ref}
          icon={icon as [string, IconName]}
          className={`fa-fw w-4 h-4 font-bold ${
            disabled
              ? "cursor-not-allowed opacity-40 text-gray-400"
              : "hover:cursor-pointer text-mk-primary hover:text-mk-primary-bright hover:shadow-2xl"
          } ${isActive ? "text-mk-primary-bright" : ""}`}
          onClick={() => !disabled && action()}
          style={{
            ...(disabled ? { pointerEvents: "none" } : {}),
            outline: "none",
            boxShadow: "none",
          }}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} // Prevents focus ring/border on click
        />
      )}
    </Whisper>
  );

  const createTextButton = (
    text: string,
    messageId: string,
    action: () => void
  ) => (
    <Whisper
      placement="top"
      controlId="control-img-hover"
      trigger={trigger}
      speaker={createTooltip(messageId)}
    >
      {(triggerProps, ref) => (
        <div
          {...triggerProps}
          ref={ref}
          onClick={() => !isDisabled && action()}
          className={`font-bold text-xl text-center ${
            isDisabled
              ? "cursor-not-allowed text-gray-400 opacity-40"
              : "hover:cursor-pointer text-mk-primary hover:text-mk-primary-bright"
          } ${editor.editor!.isActive("bold") ? "is-active" : ""}`}
          style={{
            ...(isDisabled ? { pointerEvents: "none" } : {}),
            outline: "none",
            boxShadow: "none",
          }}
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
          onMouseDown={(e) => e.preventDefault()} // Prevents focus ring/border on click
        >
          {text}
        </div>
      )}
    </Whisper>
  );

  const createMediaButton = (
    icon: [string, string],
    messageId: string,
    action: () => void,
    isActive: boolean = false
  ) => (
    <Whisper
      placement="top"
      controlId="control-img-hover"
      trigger="hover"
      speaker={createTooltip(messageId)}
    >
      <FontAwesomeIcon
        icon={icon as [IconPrefix, IconName]}
        className={`fa-fw h-4 w-4 font-bold text-mk-primary hover:cursor-pointer hover:text-mk-primary-bright hover:shadow-2xl ${
          isActive ? "text-mk-primary-bright" : "text-mk-primary"
        }`}
        onClick={() => {
          setSelectState(selectState === icon[1] ? "" : icon[1]);
          action();
        }}
      />
    </Whisper>
  );

  const createTextToVideoButton = () => {
    // Don't show text to video button for image cards
    if (
      editor.cardType === CARD_TYPES.IMG ||
      editor.cardType === CARD_TYPES.AUDIO
    ) {
      return null;
    }

    const isActive = selectState === "text-to-video" || isTextToVideoHovered;
    const iconSrc = isActive ? "/textToVideo.svg" : "/lightTextToVideo.svg";
    const isButtonDisabled = editor.videoGenerationValue;

    return (
      <Whisper
        placement="top"
        controlId="control-img-hover"
        trigger={isButtonDisabled ? undefined : "hover"}
        speaker={createTooltip("courses.cardComponent.textToVideo")}
      >
        {(triggerProps, ref) => (
          <img
            {...triggerProps}
            ref={ref}
            src={iconSrc}
            alt={intl.formatMessage({
              id: "courses.cardComponent.textToVideo",
            })}
            onMouseEnter={() => {
              if (!isButtonDisabled) setIsTextToVideoHovered(true);
            }}
            onMouseLeave={() =>
              !isButtonDisabled && setIsTextToVideoHovered(false)
            }
            onClick={() => {
              if (editor.chapterName === "") {
                console.log(
                  "chapter name empty here for show warning dialogue"
                );
                // setChapterWarningDialog(true);
                editor.warningDialogFunc(true);

                return;
              }

              !isButtonDisabled &&
                (setSelectState(
                  selectState === "text-to-video" ? "" : "text-to-video"
                ),
                editor.textToVideo());
            }}
            className={`fa-fw w-4 h-4 font-bold ${
              isButtonDisabled
                ? "cursor-not-allowed opacity-40 text-gray-400"
                : "hover:cursor-pointer text-mk-primary hover:text-mk-primary-bright hover:shadow-2xl"
            } ${isActive ? "text-mk-primary-bright" : "text-mk-primary"}`}
            style={isButtonDisabled ? { pointerEvents: "none" } : undefined}
            tabIndex={isButtonDisabled ? -1 : 0}
            aria-disabled={isButtonDisabled}
          />
        )}
      </Whisper>
    );
  };

  const createTextToImageButton = () => {
    // Don't show text to image button for video cards
    if (
      editor.cardType === CARD_TYPES.VIDEO ||
      editor.cardType === CARD_TYPES.AUDIO
    ) {
      return null;
    }

    const isActive = selectState === "text-to-image" || isTextToImageHovered;
    const iconSrc = isActive ? "/textToImage.svg" : "/lightTextToImage.svg";

    console.log("editor status", editor.status);

    return (
      <Whisper
        placement="top"
        controlId="control-img-hover"
        trigger={"hover"}
        speaker={createTooltip("courses.cardComponent.textToImage")}
      >
        {(triggerProps, ref) => (
          <img
            {...triggerProps}
            ref={ref}
            src={iconSrc}
            alt="Text to Image"
            onMouseEnter={() => setIsTextToImageHovered(true)}
            onMouseLeave={() => setIsTextToImageHovered(false)}
            onClick={() => (
              setSelectState(
                selectState === "text-to-image" ? "" : "text-to-image"
              ),
              editor.textToImage()
            )}
            className={`fa-fw w-4 h-4 font-bold ${"hover:cursor-pointer text-mk-primary hover:text-mk-primary-bright hover:shadow-2xl"} ${
              isActive ? "text-mk-primary-bright" : "text-mk-primary"
            }`}
          />
        )}
      </Whisper>
    );
  };

  const createTextToAudioButton = () => {
    // Don't show text to image button for video cards
    if (
      editor.cardType === CARD_TYPES.IMG ||
      editor.cardType === CARD_TYPES.VIDEO
    ) {
      return null;
    }

    const isActive = selectState === "text-to-audio" || isTextToAudioHovered;
    // const iconSrc = isActive ? "/textToImage.svg" : "/lightTextToImage.svg";

    const iconSrc = isActive ? "/textToAudio.svg" : "/lightTextToAudio.svg";

    console.log("editor status", editor.status);

    return (
      <Whisper
        placement="top"
        controlId="control-img-hover"
        trigger={"hover"}
        speaker={createTooltip("courses.cardComponent.textToAudio")}
      >
        {(triggerProps, ref) => (
          <img
            {...triggerProps}
            ref={ref}
            src={iconSrc}
            alt="Text to Audio"
            onMouseEnter={() => setIsTextToAudioHovered(true)}
            onMouseLeave={() => setIsTextToAudioHovered(false)}
            onClick={() => (
              setSelectState(
                selectState === "text-to-audio" ? "" : "text-to-audio"
              ),
              editor.textToAudio()
            )}
            className={`fa-fw w-4 h-4 font-bold ${"hover:cursor-pointer text-mk-primary hover:text-mk-primary-bright hover:shadow-2xl"} ${
              isActive ? "text-mk-primary-bright" : "text-mk-primary"
            }`}
          />
        )}
      </Whisper>
    );
  };

  return (
    <div>
      <div className="flex justify-center items-center mt-[-2%] pl-4 pr-4 pt-1 space-x-2">
        {createDragButton(
          ["far", "fa-arrows-up-down-left-right"],
          "courses.cardComponent.drag",
          () => {
            if (selectState === "arrow") {
              setSelectState("");
            } else {
              setSelectState("arrow");
              // editor.enableDrag(true);
              // editor.editor.setEditable(false);
            }
          },
          selectState === "arrow"
        )}

        {createIconButton(
          ["far", "fa-face-smile"],
          "courses.cardComponent.selectEmoji",
          () => (setSelectState(""), editor.emojiPicker()),
          selectState === "emoji"
        )}

        {createTextButton(
          intl.formatMessage({ id: "courses.cardComponent.B" }),
          "courses.cardComponent.bold",
          () => {
            setSelectState(selectState === "b" ? "" : "b");
            editor.editor!.chain().focus().toggleBold().run();
          }
        )}

        {editor.cardType === CARD_TYPES.IMG &&
          createMediaButton(
            ["fas", "fa-image"],
            "courses.cardComponent.imageUpload",
            editor.imageUpload,
            selectState === "img"
          )}

        {editor.cardType === CARD_TYPES.AUDIO &&
          createMediaButton(
            ["far", "fa-music"],
            "courses.cardComponent.audioUpload",
            editor.audioUpload,
            selectState === "music"
          )}

        {editor.cardType === CARD_TYPES.VIDEO &&
          createIconButton(
            ["fas", "fa-file-video"],
            "courses.cardComponent.videoUpload",
            () => {
              setSelectState(selectState === "video" ? "" : "video");
              editor.videoUpload();
            },
            selectState === "video"
          )}

        {createIconButton(
          ["far", "fa-copy"],
          "courses.courseCard.duplicateLabel",
          () => {
            setSelectState(selectState === "copy" ? "" : "copy");
            editor.duplicateFunc();
          },
          selectState === "copy",
          editor.isDuplicateDisabled || editor.videoGenerationValue
        )}

        {createIconButton(
          ["far", "fa-trash"],
          "courses.cardComponent.deleteCard",
          () => {
            setSelectState(selectState === "trash" ? "" : "trash");
            editor.deleteFunc();
          },
          selectState === "trash"
        )}

        {createTextToImageButton()}
        {createTextToVideoButton()}
        {createTextToAudioButton()}
      </div>
    </div>
  );
};

// Main CardComponent
export function CardComponent(props: CardComponentProps) {
  const intl = useIntl();
  const { style, attributes, dataItem, forwardRef } = props;
  const data = props?.dataItem as Cards;

  // State Management
  const [editorContent, setEditorContent] = useState<string>(
    whatsappToHTML(dataItem.cardInfo.card[0].text)
  );
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [deleteCard, setDeleteCard] = useState<boolean>(false);
  const [cardData, setCardData] = useState<any>();
  const [linkData, setLinkData] = useState<any>();
  const [toogleData, setToggleData] = useState<Boolean>(false);
  const [formUserDialog, setFormUserDialog] = useState<boolean>(false);
  const [duplicateImage, setDuplicateImage] = useState<boolean>(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<any>(
    props.dataItem.cardInfo.card[0].tempUrl
  );
  const [generatedImageUrl, setGeneratedImageUrl] = useState<any>(
    props.dataItem.cardInfo.card[0].tempUrl
  );
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<any>(
    props.dataItem.cardInfo.card[0].tempUrl
  );
  const [generatedAudioPrompt, setGeneratedAudioPrompt] = useState<any>(
    props.dataItem.cardInfo.card[0].text
  );
  const [audioObject, setAudioObject] = useState<any>();
  const [audioButton, setAudioButton] = useState<Boolean>(true);
  const [showSelectedFile, setSelectedFile] = useState<Boolean>(false);
  const [showChangedFile, setChangedFile] = useState<Boolean>(false);
  const [frameUrl, setFrameurl] = useState<any>();
  const [courseId, setCourseId] = useState<any>();
  const [accId, setAccId] = useState<any>(props.cards.accntId);
  const [imageUpload, setImageUpload] = useState<boolean>(false);
  const [videoUpload, setVideoUpload] = useState<boolean>(false);
  const [audioUpload, setAudioUpload] = useState<boolean>(false);
  const [imgRes, setImgRes] = useState("");
  const [aiVideoPromptDialog, setAiVideoPromptDialog] =
    useState<boolean>(false);
  const [aiImagePromptDialog, setAiImagePromptDialog] =
    useState<boolean>(false);
  const [aiAudioPromptDialog, setAiAudioPromptDialog] =
    useState<boolean>(false);
  const [inGenerate, setInGenerate] = useState<boolean>(false);
  const [inTextToVideo, setInTextToVideo] = useState<boolean>(false);
  const [videoPromptText, setVideoPromptText] = useState<string>("");
  const [imagePromptText, setImagePromptText] = useState<string>("");
  const [audioPromptText, setAudioPromptText] = useState<string>("");
  const [waitIndicationDialog, setWaitIndicationDialog] =
    useState<boolean>(false);
  const [convertedToVideo, setConvertedToVideo] = useState<boolean>(false);
  const [convertedToImage, setConvertedToImage] = useState<boolean>(false);
  const [convertedToAudio, setConvertedToAudio] = useState<boolean>(false);
  const [chapterWarningDialog, setChapterWarningDialog] =
    useState<boolean>(false);
  const [textToVideoCardType, setTextToVideoCardType] =
    useState<boolean>(false);
  const [textToAudioCardType, setTextToAudioCardType] =
    useState<boolean>(false);

  const [selectedLanguage, setSelectedLanguage] = useState<string>("english");
  const [selectedAccent, setSelectedAccent] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState<string>("male");
  const [selectedStyle, setSelectedStyle] = useState<string>("default");
  const [isAudioGenerating, setIsAudioGenerating] = useState<boolean>(false);
  const [originalTextBeforeDialog, setOriginalTextBeforeDialog] =
    useState<string>("");

  console.log("selected language", selectedLanguage);

  console.log("wait indication dialog value print", waitIndicationDialog);
  console.log("ai image prompt dialog value print", aiImagePromptDialog);
  console.log("props.dataItem.chapterName", props.dataItem?.chapterName);
  console.log("cardData.chapterName", cardData?.chapterName);

  console.log("aIVideoGenerating in card component", props.aIVideoGenerating);
  console.log("accent selected", selectedAccent);
  console.log("language selected", selectedLanguage);
  console.log("generated audio url", generatedAudioUrl);
  console.log("audio prompt dialog value", aiAudioPromptDialog);
  console.log("card data print in card component", cardData);
  console.log("editor content print", editorContent);

  // Console log the chapter name here

  // The chapter name is likely undefined because props.dataItem?.chapterName is not set at this point,
  // and cardData?.chapterName is also undefined until cardData is updated later.
  // To debug, let's log the entire dataItem and cardData objects for more context:
  console.log("chapter name print in card component", {
    "props.dataItem": props.dataItem,
    cardData: cardData,
    "props.dataItem.chapterName": props.dataItem?.chapterName,
    "cardData.chapterName": cardData?.chapterName,
  });
  // Log the URL field from dataItem
  console.log("Card URL field:", {
    cardId: dataItem.cardId,
    url: dataItem.url,
    cardInfo: dataItem.cardInfo,
    cardType: dataItem.cardType,
  });

  console.log("presigned url in card component", dataItem.url);

  // Refs
  const hiddenFileInput = useRef<any>();
  const hiddenFileVideo = useRef<any>();
  const hiddenFileAudio = useRef<any>();
  const sampleRef = useRef();

  // Media State
  const [selectedImage, setSelectedImage] = useState(() => {
    if (dataItem.cardType === CARD_TYPES.IMG)
      return dataItem.cardInfo.card[0].tempUrl;
    return null;
  });
  const [selectedVideo, setSelectedVideo] = useState(() => {
    if (dataItem.cardType === CARD_TYPES.VIDEO)
      return dataItem.cardInfo.card[0].tempUrl || null;
    return null;
  });
  const [selectedAudio, setSelectedAudio] = useState(() => {
    if (dataItem.cardType === CARD_TYPES.AUDIO)
      return dataItem.cardInfo.card[0].tempUrl || null;
    return null;
  });

  // Editor Configuration
  const editorExtensions = [
    StarterKit.configure({
      bulletList: { keepMarks: true, keepAttributes: false },
      orderedList: { keepMarks: true, keepAttributes: false },
      hardBreak: false,
    }),
    CharacterCount.configure({ limit: CHARACTER_LIMIT }),
    HardBreak.extend({
      addKeyboardShortcuts() {
        return { Enter: () => this.editor.commands.setHardBreak() };
      },
    }),
  ];

  const editor = useEditor({
    extensions: editorExtensions,
    content: editorContent,
    onUpdate: ({ editor }) => setEditorContent(editor.getHTML()),
    editable: true,
  });

  const notEditeditor = useEditor({
    extensions: editorExtensions.slice(0, -1), // Remove HardBreak for non-editable
    content: editorContent,
    onUpdate: ({ editor }) => setEditorContent(editor.getHTML()),
    editable: false,
  });

  // Utility Functions
  const handleSelectEmoji = (emoji: any) => {
    editor!.chain().focus().insertContent(emoji.native).run();
    setShowEmojiPicker(false);
  };

  const handleDuplicateCard = () => {
    if (props.handleDuplicateCard) {
      props.handleDuplicateCard(props.dataItem.cardId);
      setDuplicateImage(true);
    }
  };

  const handleClick = (event: any) => {
    setCardData(event);
    setImageUpload(false);
    hiddenFileInput.current.click();
  };

  const handleVideoClick = (e: any) => {
    setCardData(e);
    setVideoUpload(false);
    hiddenFileVideo?.current?.click();
  };

  const handleAudioClick = (e: any) => {
    setCardData(e);
    setAudioUpload(false);
    hiddenFileAudio.current.click();
  };

  const handleGenerateAiVideo = () => setAiVideoPromptDialog(true);
  const handleGenerateAiImage = () => setAiImagePromptDialog(true);
  const handleGenerateAiAudio = () => setAiAudioPromptDialog(true);
  const handleTextToVideo = () => {
    // Just open the prompt dialog with the current card's text
    // Don't convert card type yet - that will happen when generating the video
    setTextToVideoCardType(true);
    setAiVideoPromptDialog(true);
  };
  const handleTextToImage = () => {
    // Just open the prompt dialog with the current card's text
    // Don't convert card type yet - that will happen when generating the image
    setAiImagePromptDialog(true);
  };
  const handleTextToAudio = () => {
    setOriginalTextBeforeDialog(editorContent|| props.dataItem.cardInfo.card[0].text);
    setTextToAudioCardType(true);
    setAiAudioPromptDialog(true);
    setGeneratedAudioUrl("");
  };

  const handleLinkValueChange = (e: TextBoxChangeEvent) => {
    setLinkData(e);
    setToggleData(true);
    setCardData(props.dataItem);
    if (cardData) cardData.cardInfo.card[0].url = e.value;
  };

  const okClick = () => {
    setAudioButton(true);
    audioObject.pause();
    setFormUserDialog(false);
  };

  const playAudio = () => {
    setAudioButton(false);
    audioObject.play();
  };

  const pauseAudio = () => {
    setAudioButton(true);
    audioObject.pause();
  };

  // File Handling
  async function onFileChange(event: any) {
    const file = event.target.files[0];
    const extension = getFileExtension(file.name);
    const accId = props.cards.accntId;

    const url = createFilePath(
      accId,
      cardData?.courseId,
      cardData?.chapterId,
      cardData?.cardId,
      extension
    );
    const filePathUrl = url.replace("Mikros-Cards/", "");

    setFrameurl(url);
    setCourseId(cardData?.courseId);

    // Update cardData directly like in the original
    cardData.filePath = filePathUrl;
    cardData.fileName = file;
    cardData.cardInfo.card[0].url = url;
    cardData.cardInfo.card[0].fileType = extension;
    cardData.fileCardName = file.name;
    cardData.fileSize = file.size;
    props.dataItem.fileCardName = cardData.fileCardName;

    setCardData(cardData);
    setChangedFile(true);
    setSelectedFile(true);

    if (props.handleSpecificCardUpdate) {
      const url1 = await props.handleSpecificCardUpdate(cardData);
      if (props.handleCardImageUpdate && url1) {
        props.handleCardImageUpdate(cardData.cardId, url1.url);
      }
    }
  }

  async function previewAudio(link: any, audioPrompt: string, details: any) {
    console.log("audio prompt inside preview audio", audioPrompt);
    console.log("selected language print in preview audio", selectedLanguage);
    setGeneratedAudioUrl(link);
    if (props.handleCardAudioUpdate) {
      props.handleCardAudioUpdate(props.dataItem.cardId, link);
    }
    const currentCardData = cardData ?? props.dataItem;
    if (currentCardData?.cardInfo?.card?.[0]) {
      const url = createFilePath(
        accId,
        details?.courseId,
        details?.chapterId,
        details?.cardId,
        "mp3"
      );
      const updatedCardData = {
        ...currentCardData,
        cardType: CARD_TYPES.AUDIO,
        cardInfo: {
          ...currentCardData.cardInfo,
          cardType: CARD_TYPES.AUDIO, //ensure cardInfo cardType is AUDIO
          card: [
            {
              // ...currentCardData.cardInfo.card[0],
              language: selectedLanguage,
              text: audioPrompt,
              url,
              fileType: "mp3",
              tempUrl: link,
            },
            ...currentCardData.cardInfo.card.slice(1),
          ],
        },
      };
      setCardData(updatedCardData);

      console.log("Card data print in preview audio", updatedCardData);
      setConvertedToAudio(true);
      setSelectedAudio(link);
      setEditorContent(audioPrompt);

      if (editor) {
        editor.commands.setContent(audioPrompt);
      }

      if (props.handleGenAudioUrl) {
        await props.handleGenAudioUrl(
          updatedCardData.cardId,
          url,
          updatedCardData
        );
      }
    }
  }

  async function previewImage(link: any, details: any) {
    setGeneratedImageUrl(link);
    if (props.handleCardImageUpdate) {
      props.handleCardImageUpdate(props.dataItem.cardId, link);
    }

    const currentCardData = cardData ?? props.dataItem;
    if (currentCardData?.cardInfo?.card?.[0]) {
      const url = createFilePath(
        accId,
        details?.courseId,
        details?.chapterId,
        details?.cardId,
        "png"
      );
      const updatedCardData = {
        ...currentCardData,
        cardType: CARD_TYPES.IMG, // Ensure card type is IMG
        cardInfo: {
          ...currentCardData.cardInfo,
          cardType: CARD_TYPES.IMG, // Ensure cardInfo cardType is IMG
          card: [
            {
              ...currentCardData.cardInfo.card[0],
              url,
              // fileType: CARD_TYPES.IMG, // Ensure fileType is IMG
              fileType: "png",
              tempUrl: link, // Set the tempUrl to the generated image
            },
            ...currentCardData.cardInfo.card.slice(1),
          ],
        },
      };

      setCardData(updatedCardData);
      setConvertedToImage(true); // Ensure converted state is set
      setSelectedImage(link); // Set the selected image to show it

      if (props.handleGenImageUrl) {
        await props.handleGenImageUrl(updatedCardData.cardId, url);
      }
    }
  }

  async function urlFieldSet(details: any) {
    console.log("details in urlFieldSet", details);
    const currentCardData = cardData ?? props.dataItem;
    if (currentCardData?.cardInfo?.card?.[0]) {
      // const url = "dummyTempUrl";
      const url = `Mikros-Cards/acct-${details.accntId}/crse-${details.courseId}/chptr-${details.chapterId}/card-${details.cardId}/${details.cardId}.mp4`;

      console.log("Url formed in url field set", url);
      const updatedCardData = {
        ...currentCardData,
        cardType: CARD_TYPES.VIDEO, // Ensure card type is VIDEO
        cardInfo: {
          ...currentCardData.cardInfo,
          cardType: CARD_TYPES.VIDEO, // Ensure cardInfo cardType is VIDEO
          card: [
            {
              ...currentCardData.cardInfo.card[0],
              url,
              // fileType: CARD_TYPES.VIDEO, // Ensure fileType is VIDEO
              fileType: "mp4",
            },
            ...currentCardData.cardInfo.card.slice(1),
          ],
        },
      };

      setCardData(updatedCardData);
      setConvertedToVideo(true); // Ensure converted state is set
      const presignedUrl = dataItem.url;

      if (props.handleGenVideoUrl) {
        await props.handleGenVideoUrl(
          updatedCardData.cardId,
          url,
          presignedUrl
        );
      }
    }
  }

  // Effects

  const accentOptions = useMemo(() => {
    switch (selectedLanguage) {
      case "english":
        return [
          { value: "en-IN", label: intl.formatMessage({ id: "countryIndia" }) },
          {
            value: "en-US",
            label: intl.formatMessage({ id: "countryUnitedStates" }),
          },
          {
            value: "en-GB",
            label: intl.formatMessage({ id: "countryUnitedKingdom" }),
          },
          {
            value: "en-AU",
            label: intl.formatMessage({ id: "countryAustralia" }),
          },
          {
            value: "en-CA",
            label: intl.formatMessage({ id: "countryCanada" }),
          },
          {
            value: "en-IE",
            label: intl.formatMessage({ id: "countryIreland" }),
          },
        ];
      case "hindi":
        return [
          { value: "hi-IN", label: intl.formatMessage({ id: "countryIndia" }) },
        ];
      case "kannada":
        return [
          { value: "ka-IN", label: intl.formatMessage({ id: "countryIndia" }) },
        ];

      case "tamil":
        return [
          { value: "ta-IN", label: intl.formatMessage({ id: "countryIndia" }) },
          {
            value: "ta-LK",
            label: intl.formatMessage({ id: "countrySriLanka" }),
          },
          {
            value: "ta-MY",
            label: intl.formatMessage({ id: "countryMalaysia" }),
          },
          {
            value: "ta-SG",
            label: intl.formatMessage({ id: "countrySingapore" }),
          },
        ];
      default:
        return [];
    }
  }, [selectedLanguage]);

  const speakingSytleOptions = useMemo(() => {
    if (selectedLanguage === "tamil" && selectedAccent !== "ta-IN") {
      return [{ value: "default", label: "Default" }];
    }
    if (selectedGender === "male") {
      return [
        {
          value: "default",
          label: intl.formatMessage({ id: "speakingStyleDefault" }),
        },
        {
          value: "empathetic",
          label: intl.formatMessage({ id: "speakingStyleEmpathetic" }),
        },
        {
          value: "excited",
          label: intl.formatMessage({ id: "speakingStyleExcited" }),
        },
        {
          value: "relieved",
          label: intl.formatMessage({ id: "speakingStyleRelieved" }),
        },
        { value: "shy", label: intl.formatMessage({ id: "speakingStyleShy" }) },
      ];
    } else if (selectedGender === "female") {
      return [
        {
          value: "default",
          label: intl.formatMessage({ id: "speakingStyleDefault" }),
        },
        {
          value: "empathetic",
          label: intl.formatMessage({ id: "speakingStyleEmpathetic" }),
        },
        {
          value: "excited",
          label: intl.formatMessage({ id: "speakingStyleExcited" }),
        },
        {
          value: "friendly",
          label: intl.formatMessage({ id: "speakingStyleFriendly" }),
        },
        { value: "shy", label: intl.formatMessage({ id: "speakingStyleShy" }) },
        {
          value: "serious",
          label: intl.formatMessage({ id: "speakingStyleSerious" }),
        },
        {
          value: "relieved",
          label: intl.formatMessage({ id: "speakingStyleRelieved" }),
        },
        { value: "sad", label: intl.formatMessage({ id: "speakingStyleSad" }) },
      ];
    }
    return [];
  }, [selectedLanguage, selectedAccent, selectedGender]);

  const voiceMap: Record<
    string,
    {
      default: { male: string; female: string };
      overrides?: Record<string, { male: string; female: string }>;
    }
  > = {
    english: {
      default: {
        male: "DerekMultilingualNeural",
        female: "SerenaMultilingualNeural",
      },
    },
    hindi: {
      default: {
        male: "DerekMultilingualNeural",
        female: "SerenaMultilingualNeural",
      },
    },
    kannada: {
      default: {
        male: "DerekMultilingualNeural",
        female: "SerenaMultilingualNeural",
      },
    },
    tamil: {
      default: {
        male: "DerekMultilingualNeural",
        female: "SerenaMultilingualNeural",
      },
      overrides: {
        "ta-LK": { male: "KumarNeural", female: "SaranyaNeural" },
        "ta-MY": { male: "SuryaNeural", female: "KaniNeural" },
        "ta-SG": { male: "AnbuNeural", female: "VenbaNeural" },
      },
    },
  };

  //utility function to get shortName
  // const getShortName = (
  //   gender: "male" | "female",
  //   language: string,
  //   accent: string
  // ): string => {
  //   const langConfig = voiceMap[language];
  //   if (!langConfig) {
  //     return "";
  //   }
  //   if (langConfig.overrides && langConfig.overrides[accent]) {
  //     return `${accent}-${langConfig.overrides[accent][gender]}`;
  //   }
  //   return `${accent}-${langConfig.default[gender]}`;

  // };

  const getShortName = (
    gender: "male" | "female",
    language: string,
    accent: string
  ): string => {
    const langConfig = voiceMap[language];
    if (!langConfig) {
      return "";
    }

    // ✅ Tamil overrides handled first
    if (
      language === "tamil" &&
      langConfig.overrides &&
      langConfig.overrides[accent]
    ) {
      return `${accent}-${langConfig.overrides[accent][gender]}`;
    }

    const voice = langConfig.default[gender];

    // ✅ Derek & Serena should *always* be en-US
    if (
      voice === "DerekMultilingualNeural" ||
      voice === "SerenaMultilingualNeural"
    ) {
      return `en-US-${voice}`;
    }

    // ✅ Other cases: keep accent as prefix
    return `${accent}-${voice}`;
  };

  // Reset accent whenever language changes
  useEffect(() => {
    setSelectedAccent(accentOptions.length > 0 ? accentOptions[0].value : "");
  }, [accentOptions]);

  useEffect(() => {
    setSelectedStyle(
      speakingSytleOptions.length > 0 ? speakingSytleOptions[0].value : ""
    );
  }, [speakingSytleOptions]);

  useEffect(() => {
    setSelectedFile(true);
    let audio = new Audio(props.dataItem.url);
    setAudioObject(audio);
  }, [props.dataItem]);

  useEffect(() => {
    if (props.resultUrl) setImgRes(props.resultUrl?.url);
  }, [props.resultUrl]);

  useEffect(() => {
    if (props.handleCardContentChange) {
      props.handleCardContentChange(props.dataItem.cardId, editorContent);
    }
  }, [editorContent]);

  useEffect(() => {
    console.log("generated audio prompt in useEffect", generatedAudioPrompt);
    console.log(
      "Editor content in useEffect",
      props.dataItem.cardInfo.card[0].text
    );

    // setEditorContent(props.dataItem.cardInfo.card[0].text);

    // Get the text content - prioritize generated audio prompt for audio cards
    let textContent = props.dataItem.cardInfo.card[0].text;

    // If this is an audio card and we have a generated audio prompt, use that instead
    if (props.dataItem.cardType === CARD_TYPES.AUDIO && generatedAudioPrompt) {
      textContent = generatedAudioPrompt;
    }

    setEditorContent(textContent);

    // Also update the editor instance directly
    if (editor && textContent !== editor.getHTML()) {
      editor.commands.setContent(textContent);
    }

    if (props.dataItem.cardType === CARD_TYPES.IMG) {
      setSelectedImage(props.dataItem.cardInfo.card[0].tempUrl);
    } else if (props.dataItem.cardType === CARD_TYPES.VIDEO) {
      setSelectedVideo(props.dataItem.cardInfo.card[0].tempUrl);
      if (props.dataItem.cardInfo.card[0].tempUrl) {
        setGeneratedVideoUrl(props.dataItem.cardInfo.card[0].tempUrl);
      }
    } else if (props.dataItem.cardType === CARD_TYPES.AUDIO) {
      setSelectedAudio(props.dataItem.cardInfo.card[0].tempUrl);
    }

    // Reset convertedToVideo state if card type changes from parent
    if (props.dataItem.cardType !== CARD_TYPES.VIDEO) {
      setConvertedToVideo(false);
    }
    // Reset convertedToImage state if card type changes from parent
    if (props.dataItem.cardType !== CARD_TYPES.IMG) {
      setConvertedToImage(false);
    }
    if (props.dataItem.cardType !== CARD_TYPES.AUDIO) {
      setConvertedToAudio(false);
    }
  }, [props.dataItem, generatedAudioPrompt, editor]);

  useEffect(() => {
    if (editor && editorContent && editor.getHTML() !== editorContent) {
      editor.commands.setContent(editorContent);
    }
  }, [editor, editorContent]);

  useEffect(() => {
    if (!props.showCardOptions) {
      editor?.setEditable(false);
    }
  }, [props.showCardOptions]);

  useEffect(() => {
    if (aiVideoPromptDialog) {
      // Get the text from the current card (either from editor content or card data)
      const cardText = editorContent || props.dataItem.cardInfo.card[0].text;
      const cleanText = cardText.replace(/<[^>]+>/g, "").trim();
      setVideoPromptText(cleanText);
    }
  }, [aiVideoPromptDialog, props.dataItem, editorContent]);

  useEffect(() => {
    if (aiImagePromptDialog) {
      // Get the text from the current card (either from editor content or card data)
      const cardText = editorContent || props.dataItem.cardInfo.card[0].text;
      const cleanText = cardText.replace(/<[^>]+>/g, "").trim();
      setImagePromptText(cleanText);
    }
  }, [aiImagePromptDialog, props.dataItem, editorContent]);

  useEffect(() => {
    if (aiAudioPromptDialog) {
      // Get the text from the current card (either from editor content or card data)
      const cardText = editorContent || props.dataItem.cardInfo.card[0].text;
      const cleanText = cardText.replace(/<[^>]+>/g, "").trim();
      setAudioPromptText(cleanText);
    }
  }, [aiAudioPromptDialog, props.dataItem, editorContent]);

  // Handle presigned URL updates after video generation
  // useEffect(() => {
  //   if (
  //     dataItem.url &&
  //     dataItem.url !== "object not found" &&
  //     dataItem.url !== ""
  //   ) {
  //     // Video generation completed and dataItem.url has been updated with presigned URL
  //     if (props.handleGenVideoUrl) {
  //       const url = `Mikros-Cards/acct-${props.dataItem.accntId}/crse-${props.dataItem.courseId}/chptr-${props.dataItem.chapterId}/card-${props.dataItem.cardId}/${props.dataItem.cardId}_merged.mp4`;
  //       props.handleGenVideoUrl(props.dataItem.cardId, url, props.dataItem.url);
  //     }
  //   }
  // }, [dataItem.url]);

  // Computed Values
  const characterCountInCard =
    editor?.storage?.characterCount?.characters() || 0;

  // Get effective card type (considering conversion to video or image)
  const getEffectiveCardType = () => {
    if (convertedToVideo) {
      return CARD_TYPES.VIDEO;
    }
    if (convertedToImage) {
      return CARD_TYPES.IMG;
    }
    if (convertedToAudio) {
      return CARD_TYPES.AUDIO;
    }
    return props.dataItem.cardType;
  };

  const effectiveCardType = getEffectiveCardType();
  const isVideoGenerating =
    props.dataItem.cardType === CARD_TYPES.VIDEO &&
    (props?.videoGeneratingCardIds?.has(props.dataItem.cardId) ||
      dataItem.url === "object not found" ||
      dataItem.url === "");
  // props.dataItem.cardInfo?.card[0]?.url === "dummyTempUrl";
  const isCardEmpty =
    props.dataItem.cardInfo.card[0].text === "" ||
    props.dataItem.cardInfo.card[0].text === "<p></p>";
  const hasValidationError =
    (effectiveCardType === CARD_TYPES.TEXT &&
      isCardEmpty &&
      props.dataItem.bgColor === "red") ||
    (effectiveCardType !== CARD_TYPES.TEXT &&
      effectiveCardType !== CARD_TYPES.LINK &&
      props.dataItem.cardInfo.card[0].url === "" &&
      props.dataItem.bgColor === "red") ||
    (effectiveCardType === CARD_TYPES.LINK &&
      props.dataItem.url === "" &&
      props.dataItem.bgColor === "red") ||
    (effectiveCardType !== CARD_TYPES.LINK &&
      effectiveCardType !== CARD_TYPES.TEXT &&
      props.dataItem.fileSize > FILE_SIZE_LIMIT &&
      props.dataItem.bgColor === "red");

  console.log("print is video generating", isVideoGenerating);
  console.log(
    "is VideoGenerating Card Ids inside card component",
    props.videoGeneratingCardIds
  );

  console.log("dataItem.url", dataItem.url);
  console.log("is video generating or not", isVideoGenerating);

  const renderAiAudioPromptDialog = () =>
    aiAudioPromptDialog && (
      <Dialog className="promptDialogue">
        <div className="p-2 bg-white rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {intl.formatMessage({ id: "courses.cardComponent.aiAudioPrompt" })}
          </h3>

          {/* Prompt Input */}
          <Textarea
            minRows={6}
            maxRows={6}
            value={audioPromptText}
            placeholder={intl.formatMessage({
              id: "courses.cardComponent.enterAudioPrompt",
            })}
            onChange={(e: any) => setAudioPromptText(e.target.value)}
            sx={{
              backgroundColor: "var(--color-mk-white)",
              border: "1px solid var(--color-mk-primary)",
              "&::before, &::after": { display: "none !important" },
              "&:focus-within": { border: "2px solid var(--color-mk-primary)" },
            }}
          />

          {/* Audio Settings */}
          <h6 className="text-lg font-semibold mb-4 mt-4">
            {/* {intl.formatMessage({ id: "courses.cardComponent.aiAudioPrompt" })} */}
            {intl.formatMessage({ id: "cardComponent.audioSettings" })}
          </h6>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Language */}
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: "var(--color-mk-primary-bright)",
                  "&.Mui-focused": { color: "var(--color-mk-primary-bright)" },
                  "&.MuiInputLabel-shrink": {
                    color: "var(--color-mk-primary-bright)",
                  },
                }}
              >
                {intl.formatMessage({ id: "cardComponent.language" })}
              </InputLabel>
              <Select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                label={intl.formatMessage({ id: "cardComponent.language" })}
                MenuProps={{
                  disablePortal: true,
                }}
              >
                <MenuItem value="english">
                  {intl.formatMessage({ id: "languageEnglish" })}
                </MenuItem>
                <MenuItem value="hindi">
                  {intl.formatMessage({ id: "languageHindi" })}
                </MenuItem>
                <MenuItem value="tamil">
                  {intl.formatMessage({ id: "languageTamil" })}
                </MenuItem>
                <MenuItem value="kannada">
                  {intl.formatMessage({ id: "languageKannada" })}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedLanguage}>
              <InputLabel
                sx={{
                  color: "var(--color-mk-primary-bright)",
                  "&.Mui-focused": { color: "var(--color-mk-primary-bright)" },
                  "&.MuiInputLabel-shrink": {
                    color: "var(--color-mk-primary-bright)",
                  },
                }}
              >
                {intl.formatMessage({ id: "cardComponent.accent" })}
              </InputLabel>
              <Select
                value={selectedAccent}
                onChange={(e) => setSelectedAccent(e.target.value)}
                label={intl.formatMessage({ id: "cardComponent.accent" })}
                MenuProps={{ disablePortal: true }}
              >
                {accentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Gender */}
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: "var(--color-mk-primary-bright)",
                  "&.Mui-focused": { color: "var(--color-mk-primary-bright)" },
                  "&.MuiInputLabel-shrink": {
                    color: "var(--color-mk-primary-bright)",
                  },
                }}
              >
                {intl.formatMessage({ id: "cardComponent.gender" })}
              </InputLabel>
              <Select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                label={intl.formatMessage({ id: "cardComponent.gender" })}
                MenuProps={{
                  disablePortal: true,
                }}
              >
                <MenuItem value="male">
                  {intl.formatMessage({ id: "genderMale" })}
                </MenuItem>
                <MenuItem value="female">
                  {intl.formatMessage({ id: "genderFemale" })}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: "var(--color-mk-primary-bright)",
                  "&.Mui-focused": { color: "var(--color-mk-primary-bright)" },
                  "&.MuiInputLabel-shrink": {
                    color: "var(--color-mk-primary-bright)",
                  },
                }}
              >
                {intl.formatMessage({ id: "cardComponent.speakingStyle" })}
              </InputLabel>
              <Select
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                label={intl.formatMessage({
                  id: "cardComponent.speakingStyle",
                })}
                MenuProps={{ disablePortal: true }}
              >
                {speakingSytleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <h6 className="text-lg font-semibold mb-4 mt-4">
            {/* {intl.formatMessage({ id: "courses.cardComponent.aiAudioPrompt" })} */}
            {/* Generated AI Audio */}
          </h6>
          {/* <div className="bg-mk-primary-15 border border-mk-primary-bright rounded-md p-3">
            <audio
              // className="w-full h-auto rounded-xl"
              controls
              // src={
              //   generatedAudioUrl ||
              //   selectedAudio ||
              //   props.dataItem.cardInfo.card[0].tempUrl ||
              //   props.dataItem.url ||
              //   ""
              // }
              src={generatedAudioUrl}
            />
          </div> */}
          <div className="w-full flex flex-col items-start justify-start">
            <Button
              themeColor="primary"
              className="shadow-2xl font-bold mb-4"
              style={{ alignSelf: "flex-start" }}
              onClick={async () => {
                setIsAudioGenerating(true);
                setGeneratedAudioUrl("");
                const trimmedPrompt = audioPromptText.trim();
                if (!trimmedPrompt) return;

                const currentLanguage = selectedLanguage;
                console.log("Current selected language:", currentLanguage);

                // Do not close dialog or mutate card data on Test; only preview

                const gender = selectedGender === "male" ? "male" : "female";
                const shortName = getShortName(
                  gender,
                  selectedLanguage,
                  selectedAccent
                );
                const details = {
                  courseId:
                    props.dataItem?.courseId && props.dataItem?.courseId !== ""
                      ? props.dataItem.courseId
                      : cardData?.courseId && cardData?.courseId !== ""
                      ? cardData.courseId
                      : props.cards?.courseId && props.cards?.courseId !== ""
                      ? props.cards.courseId
                      : "",
                  chapterId:
                    props.dataItem?.chapterId &&
                    props.dataItem?.chapterId !== ""
                      ? props.dataItem.chapterId
                      : cardData?.chapterId && cardData?.chapterId !== ""
                      ? cardData.chapterId
                      : "",
                  cardId:
                    props.dataItem?.cardId && props.dataItem?.cardId !== ""
                      ? props.dataItem.cardId
                      : cardData?.cardId && cardData?.cardId !== ""
                      ? cardData.cardId
                      : "",
                  accntId:
                    props.dataItem?.accntId && props.dataItem?.accntId !== ""
                      ? props.dataItem.accntId
                      : cardData?.accntId && cardData?.accntId !== ""
                      ? cardData.accntId
                      : props.cards?.accntId && props.cards?.accntId !== ""
                      ? props.cards.accntId
                      : "",
                  createdBy:
                    props.dataItem?.createdBy &&
                    props.dataItem?.createdBy !== ""
                      ? props.dataItem.createdBy
                      : cardData?.createdBy && cardData?.createdBy !== ""
                      ? cardData.createdBy
                      : "",

                  prompt: trimmedPrompt,
                  cardType: textToAudioCardType ? "TEXT_TO_AUD" : "AUD",
                  language: selectedLanguage,
                  languageCode: selectedAccent,
                  style: selectedStyle,
                  voiceName: shortName,
                };

                console.log(
                  "Items being passed to handle audio generation",
                  details
                );

                // if (props.handleAudioGeneration) {
                //   try {
                //     const presignedUrl = await props.handleAudioGeneration(
                //       details
                //     );
                //     console.log("print presigned url audio", presignedUrl);
                //     setGeneratedAudioUrl(presignedUrl);
                //     // Keep the dialog open for preview
                //     // setAiAudioPromptDialog(true);
                //   } catch (error) {
                //     console.error("Error in handleAudioGeneration", error);
                //     setAiAudioPromptDialog(true);
                //   }
                // }

                if (props.handleAudioGeneration) {
                  try {
                    const result = await props.handleAudioGeneration(details);

                    if (result) {
                      console.log(
                        "print presigned url audio",
                        result.presignedUrl
                      );
                      console.log("print prompt", result.prompt);

                      setGeneratedAudioUrl(result.presignedUrl);
                      // if you also want to store prompt in state:
                      setGeneratedAudioPrompt(result.prompt);

                      // Keep the dialog open for preview
                      // setAiAudioPromptDialog(true);
                    } else {
                      console.warn("handleAudioGeneration returned false");
                    }
                  } catch (error) {
                    console.error("Error in handleAudioGeneration", error);
                    setAiAudioPromptDialog(true);
                  }
                }
              }}
              disabled={!audioPromptText.trim()}
            >
              {intl.formatMessage({
                id: "courses.cardComponent.generateAiAudio",
              })}
            </Button>

            <div className="w-full border border-mk-primary-bright rounded-lg bg-mk-primary-10 p-1 flex flex-col items-center justify-center gap-4">
              {/* Removed inner border to avoid double border */}
              <div className="bg-mk-primary-15 rounded-md p-3 flex items-center justify-center min-h-[60px] w-full mt-0">
                {(() => {
                  // If generatedAudioUrl is available, ensure isAudioGenerating is set to false
                  if (isAudioGenerating && generatedAudioUrl) {
                    setTimeout(() => setIsAudioGenerating(false), 0);
                  }

                  if (isAudioGenerating && !generatedAudioUrl) {
                    return (
                      <div className="flex items-center gap-3">
                        <CircularProgress
                          size={28}
                          style={{
                            color: "var(--color-mk-primary-bright)",
                            width: 28,
                            height: 28,
                          }}
                        />
                        <span className="text-mk-primary-bright">
                          {intl.formatMessage({
                            id: "courses.cardComponent.generatingAiAudio",
                          })}
                        </span>
                      </div>
                    );
                  } else if (generatedAudioUrl) {
                    return (
                      <audio
                        controls
                        src={generatedAudioUrl}
                        style={{ width: "100%" }}
                      />
                    );
                  } else {
                    return (
                      <span className="text-mk-primary-bright">
                        {intl.formatMessage({
                          id: "courses.cardComponent.noAudioGenerated",
                        })}
                      </span>
                    );
                  }
                })()}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() => {
                setAiAudioPromptDialog(false);
                setAudioPromptText("");
                setSelectedLanguage("english");
                setSelectedAccent("en-IN");
                setSelectedGender("male");
                setSelectedStyle("default");
                setGeneratedAudioUrl("");
                setGeneratedAudioPrompt("");
                setEditorContent(originalTextBeforeDialog);

                if(editor)
                {
                  editor.commands.setContent(originalTextBeforeDialog);
                }
              }}
              disabled={isAudioGenerating}
            >
              {intl.formatMessage({ id: "cancelButton" })}
            </Button>

            <Button
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={() => {
                setGeneratedAudioUrl("");
                console.log("I am inside save of ai audio");
                const trimmedPrompt = audioPromptText.trim();
                if (!trimmedPrompt) {
                  // Don't proceed if no prompt is provided
                  return;
                }
                setAiAudioPromptDialog(false);
                if (props.handleCardTypeUpdate) {
                  props.handleCardTypeUpdate(
                    props.dataItem.cardId,
                    CARD_TYPES.AUDIO
                  );
                }
                if (props.handleFileTypeUpdate) {
                  props.handleFileTypeUpdate(props.dataItem.cardId, "mp3");
                }
                const updatedCardData = {
                  ...props.dataItem,
                  cardType: CARD_TYPES.AUDIO,
                  cardInfo: {
                    ...props.dataItem.cardInfo,
                    cardType: CARD_TYPES.AUDIO,
                    card: [
                      {
                        ...props.dataItem.cardInfo.card[0],
                        fileType: "mp3",
                      },
                      ...props.dataItem.cardInfo.card.slice(1),
                    ],
                  },
                  fileCardName: undefined,
                };
                setCardData(updatedCardData);
                setConvertedToAudio(true);

                //set image state
                setSelectedImage(null);
                setSelectedVideo(null);
                setSelectedAudio(null);

                const details = {
                  courseId:
                    props.dataItem?.courseId && props.dataItem?.courseId !== ""
                      ? props.dataItem.courseId
                      : cardData?.courseId && cardData?.courseId !== ""
                      ? cardData.courseId
                      : props.cards?.courseId && props.cards?.courseId !== ""
                      ? props.cards.courseId
                      : "",
                  chapterId:
                    props.dataItem?.chapterId &&
                    props.dataItem?.chapterId !== ""
                      ? props.dataItem.chapterId
                      : cardData?.chapterId && cardData?.chapterId !== ""
                      ? cardData.chapterId
                      : "",
                  cardId:
                    props.dataItem?.cardId && props.dataItem?.cardId !== ""
                      ? props.dataItem.cardId
                      : cardData?.cardId && cardData?.cardId !== ""
                      ? cardData.cardId
                      : "",
                  accntId:
                    props.dataItem?.accntId && props.dataItem?.accntId !== ""
                      ? props.dataItem.accntId
                      : cardData?.accntId && cardData?.accntId !== ""
                      ? cardData.accntId
                      : props.cards?.accntId && props.cards?.accntId !== ""
                      ? props.cards.accntId
                      : "",
                  createdBy:
                    props.dataItem?.createdBy &&
                    props.dataItem?.createdBy !== ""
                      ? props.dataItem.createdBy
                      : cardData?.createdBy && cardData?.createdBy !== ""
                      ? cardData.createdBy
                      : "",

                  prompt: trimmedPrompt,
                  cardType: textToAudioCardType ? "TEXT_TO_AUD" : "AUD",
                };

                if (
                  generatedAudioUrl &&
                  typeof generatedAudioUrl === "string"
                ) {
                  console.log("Calling previewAudio with", generatedAudioUrl);
                  previewAudio(
                    generatedAudioUrl,
                    generatedAudioPrompt,
                    details
                  );
                } else {
                  console.log("generated audio url is falsy or not a string");
                  if (props.handleCardTypeUpdate) {
                    props.handleCardTypeUpdate(
                      props.dataItem.cardId,
                      CARD_TYPES.TEXT
                    );
                  }
                }
              }}
              disabled={!generatedAudioUrl || !audioPromptText.trim()}
            >
              {intl.formatMessage({ id: "saveButton" })}
            </Button>
          </div>
        </div>
      </Dialog>
    );

  const renderAiImagePromptDialog = () =>
    aiImagePromptDialog && (
      <Dialog className="promptDialogue">
        <div className="p-2 bg-white rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {intl.formatMessage({ id: "courses.cardComponent.aiImagePrompt" })}
          </h3>
          <Textarea
            minRows={6}
            maxRows={6}
            value={imagePromptText}
            placeholder={intl.formatMessage({
              id: "courses.cardComponent.enterImagePrompt",
            })}
            onChange={(e: any) => setImagePromptText(e.target.value)}
            sx={{
              backgroundColor: "var(--color-mk-white)",
              border: "1px solid var(--color-mk-primary)",
              "&::before, &::after": { display: "none !important" },
              "&:focus-within": { border: "2px solid var(--color-mk-primary)" },
            }}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() => {
                setAiImagePromptDialog(false);
                setImagePromptText("");
              }}
            >
              {intl.formatMessage({ id: "cancelButton" })}
            </Button>
            <Button
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={async () => {
                // Check if prompt text is empty or only whitespace
                const trimmedPrompt = imagePromptText.trim();
                if (!trimmedPrompt) {
                  // Don't proceed if no prompt is provided
                  return;
                }

                setAiImagePromptDialog(false);

                // Convert card to image type first
                if (props.handleCardTypeUpdate) {
                  props.handleCardTypeUpdate(
                    props.dataItem.cardId,
                    CARD_TYPES.IMG
                  );
                }
                if (props.handleFileTypeUpdate) {
                  props.handleFileTypeUpdate(props.dataItem.cardId, "png");
                }

                // Update local card data to reflect image type
                const updatedCardData = {
                  ...props.dataItem,
                  cardType: CARD_TYPES.IMG,
                  cardInfo: {
                    ...props.dataItem.cardInfo,
                    cardType: CARD_TYPES.IMG,
                    card: [
                      {
                        ...props.dataItem.cardInfo.card[0],
                        // fileType: CARD_TYPES.IMG,
                        fileType: "png",
                      },
                      ...props.dataItem.cardInfo.card.slice(1),
                    ],
                  },
                  fileCardName: undefined, // Clear fileCardName for AI generated image
                };

                setCardData(updatedCardData);
                setConvertedToImage(true);

                // Set image state
                setSelectedImage(null);
                setSelectedVideo(null);
                setSelectedAudio(null);

                setCourseId(cardData?.courseId);

                const details = {
                  courseId:
                    props.dataItem?.courseId && props.dataItem?.courseId !== ""
                      ? props.dataItem.courseId
                      : cardData?.courseId && cardData?.courseId !== ""
                      ? cardData.courseId
                      : props.cards?.courseId && props.cards?.courseId !== ""
                      ? props.cards.courseId
                      : "",
                  chapterId:
                    props.dataItem?.chapterId &&
                    props.dataItem?.chapterId !== ""
                      ? props.dataItem.chapterId
                      : cardData?.chapterId && cardData?.chapterId !== ""
                      ? cardData.chapterId
                      : "",
                  cardId:
                    props.dataItem?.cardId && props.dataItem?.cardId !== ""
                      ? props.dataItem.cardId
                      : cardData?.cardId && cardData?.cardId !== ""
                      ? cardData.cardId
                      : "",
                  accntId:
                    props.dataItem?.accntId && props.dataItem?.accntId !== ""
                      ? props.dataItem.accntId
                      : cardData?.accntId && cardData?.accntId !== ""
                      ? cardData.accntId
                      : props.cards?.accntId && props.cards?.accntId !== ""
                      ? props.cards.accntId
                      : "",
                  createdBy:
                    props.dataItem?.createdBy &&
                    props.dataItem?.createdBy !== ""
                      ? props.dataItem.createdBy
                      : cardData?.createdBy && cardData?.createdBy !== ""
                      ? cardData.createdBy
                      : "",

                  prompt: trimmedPrompt,
                  cardType: "IMG",
                };

                console.log(
                  "Items being passed to handle image generation",
                  details
                );

                if (props.handleImageGeneration) {
                  console.log("inside handle image generation calling");
                  try {
                    const presignedUrl = await props.handleImageGeneration(
                      details
                    );
                    console.log("print presigned url received", presignedUrl);
                    console.log("presignedUrl type:", typeof presignedUrl);
                    console.log("presignedUrl value:", presignedUrl);
                    if (presignedUrl && typeof presignedUrl === "string") {
                      console.log("Calling previewImage with:", presignedUrl);
                      previewImage(presignedUrl, details);
                    } else {
                      console.log(
                        "presignedUrl is falsy or not a string, not calling preview image. Value:",
                        presignedUrl
                      );
                      if (props.handleCardTypeUpdate) {
                        props.handleCardTypeUpdate(
                          props.dataItem.cardId,
                          CARD_TYPES.TEXT
                        );
                      }
                    }
                  } catch (error) {
                    console.error("Error in handleImageGeneration:", error);
                  }
                }
              }}
              disabled={!imagePromptText.trim()}
            >
              {intl.formatMessage({
                id: "courses.cardComponent.generateAiImage",
              })}
            </Button>
          </div>
        </div>
      </Dialog>
    );

  const renderAiVideoPromptDialog = () =>
    aiVideoPromptDialog && (
      <Dialog className="promptDialogue">
        <div className="p-2 bg-white rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {intl.formatMessage({ id: "courses.cardComponent.aiVideoPrompt" })}
          </h3>
          <Textarea
            minRows={6}
            maxRows={6}
            value={videoPromptText}
            placeholder={intl.formatMessage({
              id: "courses.cardComponent.enterVideoPrompt",
            })}
            onChange={(e: any) => setVideoPromptText(e.target.value)}
            sx={{
              backgroundColor: "var(--color-mk-white)",
              border: "1px solid var(--color-mk-primary)",
              "&::before, &::after": { display: "none !important" },
              "&:focus-within": { border: "2px solid var(--color-mk-primary)" },
            }}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() => {
                setAiVideoPromptDialog(false);
                setVideoPromptText("");
              }}
            >
              {intl.formatMessage({ id: "cancelButton" })}
            </Button>
            <Button
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={async () => {
                // Check if prompt text is empty or only whitespace

                const trimmedPrompt = videoPromptText.trim();
                if (!trimmedPrompt) {
                  // Don't proceed if no prompt is provided
                  return;
                }

                setWaitIndicationDialog(true);
                setAiVideoPromptDialog(false);
              }}
              disabled={!videoPromptText.trim()}
            >
              {intl.formatMessage({
                id: "courses.cardComponent.generateAiVideo",
              })}
            </Button>
          </div>
        </div>
      </Dialog>
    );

  const renderWaitIndicationDialog = () => {
    console.log(
      "I am inside render wait indication dialog",
      waitIndicationDialog
    );
    if (!waitIndicationDialog) return null;
    return (
      <Dialog className="confirmSuccessDialogue">
        <Alert
          description={intl.formatMessage({
            id: "courses.cardComponent.waitIndication",
          })}
          buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
          icon="successShape.png"
          iconMsg={intl.formatMessage({ id: "iconMsgSuccess" })}
          backgroundColor="mk-bright-green"
          onClick={async () => {
            // Check if prompt text is empty or only whitespace
            console.log("inside ok of video generation");
            props.setAIVideoGenerating(true);
            const trimmedPrompt = videoPromptText.trim();
            if (!trimmedPrompt) {
              // Don't proceed if no prompt is provided
              setWaitIndicationDialog(false);
              setAiVideoPromptDialog(false);
              return;
            }

            setWaitIndicationDialog(false);
            setAiVideoPromptDialog(false);
            // Start the video generation process when OK is clicked
            if (props.setVideoGeneratingCardIds) {
              props.setVideoGeneratingCardIds(props.dataItem.cardId);
            }
            // Trigger the actual video generation here
            if (props.handleVideoGeneration && trimmedPrompt) {
              const details = {
                courseName:
                  props.dataItem?.courseName ||
                  cardData?.courseName ||
                  props.cards?.courseName ||
                  "",
                chapterName:
                  props.dataItem?.chapterName ||
                  cardData?.chapterName ||
                  props.chapterName ||
                  "",
                // userId:props.dataItem?.userId,

                courseId:
                  props.dataItem?.courseId ||
                  cardData?.courseId ||
                  props.cards?.courseId ||
                  "",
                chapterId:
                  props.dataItem?.chapterId || cardData?.chapterId || "",
                cardId: props.dataItem?.cardId || cardData?.cardId || "",
                accntId:
                  props.dataItem?.accntId ||
                  cardData?.accntId ||
                  props.cards?.accntId ||
                  "",
                createdBy:
                  props.dataItem?.createdBy || cardData?.createdBy || "",
                prompt: trimmedPrompt,
                // cardType: "VID", // Always set to VIDEO for AI generation
                cardType: textToVideoCardType ? "TEXT_TO_VID" : "VID",
              };

              // Convert card to video type first
              if (props.handleCardTypeUpdate) {
                props.handleCardTypeUpdate(
                  props.dataItem.cardId,
                  CARD_TYPES.VIDEO
                );
              }
              if (props.handleFileTypeUpdate) {
                props.handleFileTypeUpdate(props.dataItem.cardId, "mp4");
              }

              // Update local card data to reflect video type
              const updatedCardData = {
                ...props.dataItem,
                cardType: CARD_TYPES.VIDEO,
                cardInfo: {
                  ...props.dataItem.cardInfo,
                  cardType: CARD_TYPES.VIDEO,
                  card: [
                    {
                      ...props.dataItem.cardInfo.card[0],
                      // fileType: CARD_TYPES.VIDEO,
                      fileType: "mp4",
                    },
                    ...props.dataItem.cardInfo.card.slice(1),
                  ],
                },
                fileCardName: undefined, // Clear fileCardName for AI generated video
              };

              setCardData(updatedCardData);
              setConvertedToVideo(true);

              // Set video state
              setSelectedVideo(null);
              setSelectedImage(null);
              setSelectedAudio(null);

              setCourseId(cardData?.courseId);

              console.log("details before urlFieldSet", details);
              urlFieldSet(details);

              // Start the video generation
              props.handleVideoGeneration(details).catch((error: any) => {
                console.error("Error in handleVideoGeneration:", error);
              });
              // await props.quickSave?.(undefined, true);
              props.handleCardUpdate?.(updatedCardData);
            }
          }}
        />
      </Dialog>
    );
  };

  const renderDeleteCardDialog = () =>
    deleteCard && (
      <Dialog className="alertDialogue">
        <Confirm
          description={intl.formatMessage({
            id: "courses.cardComponent.deleteCardAlert",
          })}
          successText={intl.formatMessage({
            id: "users.cohortList.deletionSuccess",
          })}
          failureText={intl.formatMessage({ id: "users.cohortDisplay.cancel" })}
          icon="alertShape.png"
          iconMsg={intl.formatMessage({ id: "iconMsgAlert" })}
          backgroundColor="mk-orange"
          onSuccess={() => {
            setDeleteCard(false);
            if (props.removeCard) props.removeCard(props.dataItem.cardId);
          }}
          onFailure={() => setDeleteCard(false)}
        />
      </Dialog>
    );

  const renderLinkInput = () =>
    effectiveCardType === CARD_TYPES.LINK && (
      <div className="grid grid-cols-6">
        <div className="col-start-1 col-end-7">
          <TextBox
            valid={true}
            className="w-[100%]"
            value={props.dataItem.cardInfo.card[0].url}
            placeholder={intl.formatMessage({ id: "enterLinkPlaceholder" })}
            onChange={handleLinkValueChange}
            disabled={!props.showCardOptions}
          />
          {props.showCardOptions && <div className="h-4"></div>}
        </div>
      </div>
    );

  const renderCardContent = () => {
    const isMediaCard = [
      CARD_TYPES.IMG,
      CARD_TYPES.VIDEO,
      CARD_TYPES.AUDIO,
    ].includes(effectiveCardType);

    if (!isMediaCard) {
      return (
        <div>
          <div className="absolute top-0 right-0 p-2 z-50 text-[10px]">
            {props.index(data.cardId) + "/" + props.maxCards}
          </div>
          <div className="absolute bottom-0 right-0 p-2 z-50 text-[10px]">
            {characterCountInCard + "/" + CHARACTER_LIMIT}
          </div>
          <EditorContent
            className={`editOnlyEditor rounded-xl cursor-text absolute top-0 left-0 right-0 bottom-0 w-full p-4 ${
              editor?.isFocused ? "bg-white" : ""
            } h-full bg-transparent`}
            editor={editor}
          />
        </div>
      );
    }

    // Only show media overlay when there's actual media content
    const hasMediaContent =
      (effectiveCardType === CARD_TYPES.IMG &&
        (selectedImage || props.dataItem.cardInfo.card[0].tempUrl)) ||
      (effectiveCardType === CARD_TYPES.VIDEO &&
        (selectedVideo || props.dataItem.cardInfo.card[0].tempUrl)) ||
      (effectiveCardType === CARD_TYPES.AUDIO &&
        (selectedAudio || props.dataItem.cardInfo.card[0].tempUrl)) ||
      (props.dataItem.cardInfo.card[0].url &&
        !selectedImage &&
        !selectedVideo &&
        !selectedAudio);

    if (!hasMediaContent) {
      return null; // Don't render overlay if no media content
    }

    const renderMediaOverlay = () => {
      const mediaProps = {
        [CARD_TYPES.IMG]: {
          element: (
            <img
              src={
                props.dataItem.cardInfo.card[0].tempUrl || props.dataItem.url
              }
              className="w-full rounded-xl"
            />
          ),
          editorClass:
            "editOnlyEditor rounded-xl absolute top-0 left-0 w-full h-full p-4 cursor-text text-white text-overlay",
        },
        [CARD_TYPES.VIDEO]: {
          element: (
            <video
              className="w-full h-full rounded-xl"
              controls
              controlsList="nodownload"
              src={generatedVideoUrl || props.dataItem.url}
            />
          ),
          editorClass:
            "videoOnlyEditor rounded-tl-xl rounded-tr-xl absolute top-0 left-0 w-full p-4 cursor-text text-white text-overlay",
        },
        [CARD_TYPES.AUDIO]: {
          element: (
            <audio
              className="w-full h-full rounded-xl"
              controls
              controlsList="nodownload"
              src={props.dataItem.url}
            />
          ),
          editorClass:
            "videoOnlyEditor rounded-tl-xl rounded-tr-xl absolute top-0 left-0 w-full p-4 cursor-text text-black",
        },
      } as const;

      const currentMedia =
        mediaProps[effectiveCardType as keyof typeof mediaProps];
      if (!currentMedia) return null;

      return (
        <div className="relative h-full rounded-xl">
          <div className="head-text">
            <div className={`head-${effectiveCardType.toLowerCase()}`}>
              <div
                className={`absolute top-0 right-0 p-2 z-50 text-[10px]${
                  effectiveCardType !== CARD_TYPES.AUDIO ? " text-white" : ""
                }`}
              >
                {/* {props.index(data.cardId) + 1 + "/" + props.maxCards} */}
                {props.index(data.cardId) + "/" + props.maxCards}
              </div>
              {effectiveCardType === CARD_TYPES.AUDIO ? (
                <div className="absolute top-0 left-0 p-2 z-50 text-[10px]">
                  {characterCountInCard + "/" + CHARACTER_LIMIT}
                </div>
              ) : (
                <div className="absolute bottom-0 right-0 p-2 z-50 text-[10px] text-white">
                  {characterCountInCard + "/" + CHARACTER_LIMIT}
                </div>
              )}
              <div className="overflow-hidden rounded-xl cardHeight">
                {currentMedia.element}
              </div>
            </div>
            <EditorContent
              className={currentMedia.editorClass}
              editor={editor}
            />
          </div>
        </div>
      );
    };

    return renderMediaOverlay();
  };

  const renderViewOnlyContent = () => {
    const isMediaCard = [
      CARD_TYPES.IMG,
      CARD_TYPES.VIDEO,
      CARD_TYPES.AUDIO,
    ].includes(effectiveCardType);

    if (!isMediaCard) {
      return (
        <div>
          <div className="absolute top-0 right-0 p-2 z-50 text-[10px]">
            {props.index(data.cardId) + 1 + "/" + props.maxCards}
          </div>
          <EditorContent
            className="viewOnlyEditor bg-transparent"
            editor={notEditeditor}
            contentEditable={false}
          />
        </div>
      );
    }

    // Only show media content when there's actual media (URL or tempUrl exists)
    if (!props.dataItem.url && !props.dataItem.cardInfo.card[0].tempUrl) {
      return null; // Don't render media content if no URL or tempUrl
    }

    const mediaElements = {
      [CARD_TYPES.IMG]: (
        <img
          className="w-full rounded-xl"
          src={props.dataItem.cardInfo.card[0].tempUrl || props.dataItem.url}
        />
      ),
      [CARD_TYPES.VIDEO]: (
        <video
          className="w-full h-full rounded-xl"
          controls
          src={
            props.dataItem.cardInfo.card[0].tempUrl ||
            generatedVideoUrl ||
            props.dataItem.url
          }
        />
      ),
      [CARD_TYPES.AUDIO]: (
        <audio
          className="w-full h-full rounded-xl"
          controls
          src={props.dataItem.cardInfo.card[0].tempUrl || props.dataItem.url}
        />
      ),
    };

    const editorClasses = {
      [CARD_TYPES.IMG]:
        "imageViewOnlyEditor w-full rounded-xl absolute top-0 left-0 p-4 text-white text-overlay-view",
      [CARD_TYPES.VIDEO]:
        "videoViewOnlyEditor rounded-tl-xl rounded-tr-xl absolute top-0 left-0 w-full p-4 text-white text-overlay-view-video",
      [CARD_TYPES.AUDIO]:
        "videoViewOnlyEditor rounded-tl-xl rounded-tr-xl absolute top-0 left-0 w-full p-4 text-black",
    };

    return (
      <div className="relative rounded-xl">
        <div className="head-text">
          <div>
            <div
              className={`absolute top-0 right-0 p-2 z-50 text-[10px] ${
                effectiveCardType === CARD_TYPES.AUDIO
                  ? "text-black"
                  : "text-white"
              }`}
            >
              {props.index(data.cardId) + 1 + "/" + props.maxCards}
            </div>
            <div className="overflow-hidden rounded-xl viewCardHeight">
              {mediaElements[effectiveCardType as keyof typeof mediaElements]}
            </div>
          </div>
          <EditorContent
            className={
              editorClasses[effectiveCardType as keyof typeof editorClasses]
            }
            editor={notEditeditor}
            disabled={true}
            contentEditable={false}
          />
        </div>
      </div>
    );
  };

  const renderUploadButtons = () => {
    if (
      !props.showCardOptions ||
      [CARD_TYPES.TEXT, CARD_TYPES.LINK].includes(effectiveCardType)
    )
      return null;

    const uploadButtons = {
      [CARD_TYPES.IMG]: {
        condition:
          !props.dataItem.cardInfo.card[0].url &&
          !props.dataItem.cardInfo.card[0].tempUrl &&
          !selectedImage,
        button: (
          <div className="flex flex-col gap-4 items-center">
            <Button
              rounded="full"
              themeColor="primary"
              className="pl-5 pr-5 pt-6 pb-6 shadow-2xl font-bold"
              onClick={() => handleClick(props.dataItem)}
            >
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.upload" })}
              </div>
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.image" })}
              </div>
            </Button>
            <Button
              rounded="medium"
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={handleGenerateAiImage}
              // disabled={typeof props.dataItem.status === "undefined"}
            >
              <div>
                {intl.formatMessage({
                  id: "courses.cardComponent.generateAiImage",
                })}
              </div>
            </Button>
          </div>
        ),
      },
      [CARD_TYPES.VIDEO]: {
        condition:
          !selectedVideo &&
          !props.dataItem.url &&
          !props.dataItem.cardInfo.card[0].tempUrl,
        button: (
          <div className="flex flex-col gap-4 items-center">
            <Button
              rounded="full"
              themeColor="primary"
              className="pl-5 pr-5 pt-6 pb-6 shadow-2xl font-bold"
              onClick={() => handleVideoClick(props?.dataItem)}
            >
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.upload" })}
              </div>
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.video" })}
              </div>
            </Button>
            <Button
              rounded="medium"
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={() => {
                if (props.chapterName === "") {
                  console.log(
                    "chapter name empty here for show warning dialogue"
                  );
                  setChapterWarningDialog(true);

                  return;
                }
                handleGenerateAiVideo();
              }}
              // disabled={typeof props.dataItem.status === "undefined"}
            >
              <div>
                {intl.formatMessage({
                  id: "courses.cardComponent.generateAiVideo",
                })}
              </div>
            </Button>
          </div>
        ),
      },
      [CARD_TYPES.AUDIO]: {
        condition:
          !selectedAudio &&
          !props.dataItem.url &&
          !props.dataItem.cardInfo.card[0].tempUrl,
        button: (
          // <Button
          //   rounded="full"
          //   themeColor="primary"
          //   className="pl-5 pr-5 pt-6 pb-6 shadow-2xl font-bold"
          //   onClick={() => handleAudioClick(props.dataItem)}
          // >
          //   <div>
          //     {intl.formatMessage({ id: "courses.cardComponent.upload" })}
          //   </div>
          //   <div>
          //     {intl.formatMessage({ id: "courses.cardComponent.audio" })}
          //   </div>
          // </Button>

          <div className="flex flex-col gap-4 items-center">
            <Button
              rounded="full"
              themeColor="primary"
              className="pl-5 pr-5 pt-6 pb-6 shadow-2xl font-bold"
              onClick={() => handleAudioClick(props.dataItem)}
            >
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.upload" })}
              </div>
              <div>
                {intl.formatMessage({ id: "courses.cardComponent.audio" })}
              </div>
            </Button>
            <Button
              rounded="medium"
              themeColor="primary"
              className="shadow-2xl font-bold"
              onClick={() => {
                // if (props.chapterName === "") {
                //   console.log(
                //     "chapter name empty here for show warning dialogue"
                //   );
                //   setChapterWarningDialog(true);

                //   return;
                // }
                setAudioPromptText("");
                setGeneratedAudioPrompt("");
                handleGenerateAiAudio();
              }}
              // disabled={typeof props.dataItem.status === "undefined"}
            >
              <div>
                {intl.formatMessage({
                  id: "courses.cardComponent.generateAiAudio",
                })}
              </div>
            </Button>
          </div>
        ),
      },
    } as const;

    const currentUpload =
      uploadButtons[effectiveCardType as keyof typeof uploadButtons];
    if (!currentUpload?.condition) return null;

    return (
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        {currentUpload.button}
      </div>
    );
  };

  const renderFileInputs = () =>
    props.showCardOptions &&
    [CARD_TYPES.IMG, CARD_TYPES.VIDEO, CARD_TYPES.AUDIO].includes(
      effectiveCardType
    ) && (
      <div>
        {effectiveCardType === CARD_TYPES.IMG && (
          <div>
            <div
              className=""
              onClick={(e) => {
                hiddenFileInput?.current?.click();
              }}
            ></div>
            <input
              className="hidden"
              type="file"
              hidden
              ref={hiddenFileInput}
              onChange={onFileChange}
              accept=".png,.jpeg,.PNG,.JPEG,.jpg"
            />
          </div>
        )}

        {effectiveCardType === CARD_TYPES.VIDEO && (
          <div>
            <div
              className=""
              onClick={(e) => {
                hiddenFileVideo?.current?.click();
              }}
            ></div>
            <input
              className="hidden"
              type="file"
              hidden
              ref={hiddenFileVideo}
              onChange={onFileChange}
              accept=".mp4,.MP4,.3gp"
            />
          </div>
        )}

        {effectiveCardType === CARD_TYPES.AUDIO && (
          <div>
            <div
              className=""
              onClick={(e) => {
                hiddenFileAudio?.current?.click();
              }}
            ></div>
            <input
              type="file"
              hidden
              ref={hiddenFileAudio}
              onChange={onFileChange}
              accept=".mp3,.MP3,.acc,.ACC"
            />
          </div>
        )}
      </div>
    );

  const renderMediaDialogs = () => {
    if (!formUserDialog) return null;

    const dialogContent = {
      [CARD_TYPES.IMG]: (
        <img
          src={props.dataItem.cardInfo.card[0].tempUrl || props.dataItem.url}
          width={400}
          height={400}
        />
      ),
      [CARD_TYPES.VIDEO]: (
        <video
          src={
            props.dataItem.cardInfo.card[0].tempUrl ||
            generatedVideoUrl ||
            props.dataItem.url
          }
          width="1300"
          height="1300"
          controls
        />
      ),
      [CARD_TYPES.AUDIO]: (
        <div>
          {audioButton ? (
            <FontAwesomeIcon
              icon={["far", "play" as IconName]}
              className="fa-fa w-10 h-10 cursor-pointer"
              color="black"
              onClick={playAudio}
            />
          ) : (
            <FontAwesomeIcon
              icon={["far", "pause" as IconName]}
              className="fa-fa w-10 h-10"
              color="black"
              onClick={pauseAudio}
            />
          )}
        </div>
      ),
    };

    return (
      <Dialog
        height={effectiveCardType === CARD_TYPES.AUDIO ? 208 : 400}
        width={effectiveCardType === CARD_TYPES.IMG ? 400 : 600}
      >
        <div className="grid justify-items-end">
          <div>
            <FontAwesomeIcon
              icon={["far", "close" as IconName]}
              className="fa-fa w-3 h-3 cursor-pointer"
              color="black"
              onClick={okClick}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", margin: "30px" }}>
          {(() => {
            if (effectiveCardType === CARD_TYPES.IMG) {
              return dialogContent[CARD_TYPES.IMG];
            }
            if (effectiveCardType === CARD_TYPES.VIDEO) {
              return dialogContent[CARD_TYPES.VIDEO];
            }
            if (effectiveCardType === CARD_TYPES.AUDIO) {
              return dialogContent[CARD_TYPES.AUDIO];
            }
            return null;
          })()}
        </div>
      </Dialog>
    );
  };

  return (
    console.log(
      "is video generating value just before circular progress",
      isVideoGenerating
    ),
    (
      <div
        ref={props.enableDrag ? forwardRef : sampleRef}
        {...attributes}
        style={props.enableDrag ? style : {}}
        className="mx-2"
      >
        {renderAiVideoPromptDialog()}
        {renderAiImagePromptDialog()}
        {renderAiAudioPromptDialog()}
        {waitIndicationDialog && renderWaitIndicationDialog()}
        {renderDeleteCardDialog()}

        <div>
          {showEmojiPicker && (
            <Picker
              emojiSize={24}
              emojiButtonSize={32}
              previewPosition="none"
              onEmojiSelect={handleSelectEmoji}
            />
          )}
        </div>

        <div
          className={`${
            showEmojiPicker
              ? "hidden"
              : props.showCardOptions
              ? "bg-mk-primary-20 relative w-72 cardHeight flex flex-col rounded-xl shadow-md"
              : "bg-mk-primary-20 relative w-52 h-80 flex flex-col rounded-xl shadow-md"
          } ${hasValidationError ? "border-2 border-rose-600" : ""}`}
        >
          {renderLinkInput()}

          {props.showCardOptions
            ? renderCardContent()
            : renderViewOnlyContent()}

          {isVideoGenerating === true && (
            <div className="absolute inset-0 bg-gray-500 bg-opacity-70 z-50 gap-4 flex items-center justify-center rounded-xl">
              <CircularProgress size={24} style={{ color: "white" }} />
              <div
                className={`text-white ${
                  props.showCardOptions ? "text-base" : "text-sm"
                }`}
              >
                {intl.formatMessage({
                  id: "courses.cardComponent.videoGenerationProgress",
                })}
                ...
              </div>
            </div>
          )}

          {chapterWarningDialog && (
            <Dialog className="warningDialogue">
              <Alert
                description={`${intl.formatMessage({
                  id: "courses.cardComponent.chapterWarningDialogue",
                })}`}
                buttonText={`${intl.formatMessage({
                  id: "users.buttonText.Ok",
                })}`}
                icon={"warningShape.png"}
                iconMsg={intl.formatMessage({
                  id: "iconMsgWarning",
                })}
                backgroundColor={"mk-orange"}
                onClick={() => {
                  setChapterWarningDialog(false);
                }}
              />
            </Dialog>
          )}

          {renderUploadButtons()}
          {renderFileInputs()}
        </div>

        <div className="pb-[2%]"></div>

        {props.showCardOptions && (
          <MenuBar
            enableDrag={props.handleEnableDrag}
            duplicateFunc={handleDuplicateCard}
            imageUpload={() => handleClick(props.dataItem)}
            videoUpload={() => handleVideoClick(props.dataItem)}
            audioUpload={() => handleAudioClick(props.dataItem)}
            editor={editor!}
            deleteFunc={() => setDeleteCard(true)}
            textToVideo={handleTextToVideo}
            textToImage={handleTextToImage}
            textToAudio={handleTextToAudio}
            emojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
            tooltip={null}
            cardType={effectiveCardType}
            selectedImage={selectedImage}
            selectedVideo={selectedVideo}
            status={props?.dataItem?.status}
            videoGenerationValue={isVideoGenerating}
            isDuplicateDisabled={
              ([CARD_TYPES.IMG, CARD_TYPES.VIDEO, CARD_TYPES.AUDIO].includes(
                effectiveCardType
              ) &&
                !props.dataItem.cardInfo.card[0].tempUrl &&
                !props.dataItem.cardInfo.card[0].url) ||
              isVideoGenerating
            }
            chapterName={props.chapterName}
            warningDialogFunc={setChapterWarningDialog}
          />
        )}

        {renderMediaDialogs()}
      </div>
    )
  );
}
